import React, { useMemo, useState } from 'react';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { chargeTransactionsKey } from '@dashboard/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import EmptyState from '@dashboard/components/EmptyState';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import Dialog from '@dashboard/components/Dialog';
import { DataGrid } from '@dashboard/components/DataGrid';
import { getTransactionColumns } from '@dashboard/components/TransactionColumns';
import { formatDateTime } from '@/dashboard/lib/format-date';
import getRefundReasonStatusLabel from '@/dashboard/services/status/getRefundReasonStatusLabel';

const PaymentsChargesDetailTransactions = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const { sellerId, token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: chargeTransactionsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${payment._id}/transactions`, token),
    enabled: !!payment._id,
  });

  const transactions = data?.transactions ?? [];

  const hasRefundedTransactions = transactions.some(
    t => t.status === 'refunded' || t.status === 'partially_refunded',
  );

  const mergeDate = new Date('2025-06-19T02:34:18.000Z');
  const paymentCreatedBeforeTransactionFeature = new Date(payment.createdAt) < mergeDate;

  const columns = useMemo(
    () =>
      getTransactionColumns({
        hasRefundedTransactions,
        onViewReason: t => {
          setTransaction(t);
          setOpen(true);
        },
      }),
    [hasRefundedTransactions],
  );

  const emptyState = (
    <EmptyState>
      <EmptyState.Subtitle>No transactions found for this payment.</EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4 pt-2">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader className="mb-4">
            <CardHeaderTitle>Transactions</CardHeaderTitle>
          </CardHeader>

          <CardContent>
            {isError ? (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertDescription>Failed to load transactions</AlertDescription>
              </Alert>
            ) : (
              <DataGrid
                columns={columns}
                data={transactions}
                isLoading={isLoading}
                emptyState={emptyState}
              />
            )}
          </CardContent>
        </Card>

        {paymentCreatedBeforeTransactionFeature && !isLoading && (
          <div className="text-sm text-gray-500 dark:text-muted-foreground">
            Transaction history unavailable before {formatDateTime(mergeDate)}
          </div>
        )}

        <Dialog
          cancelButton={null}
          confirmButton={null}
          size="md"
          open={open}
          onCancel={() => {
            setOpen(false);
            setTransaction(null);
          }}
        >
          <div className="flex-row">
            <div className="font-bold">Refund reason</div>
            <div className="mb-4">{getRefundReasonStatusLabel(transaction?.reason)}</div>
            {transaction?.note && (
              <>
                <div className="font-bold">Note</div>
                <div>{transaction?.note}</div>
              </>
            )}
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentsChargesDetailTransactions;
